/**
 * @name: PDP Image Lightbox
 * @description: Swiper lightbox for PDP media — desktop modal with thumbs, mobile vertical scroll
 * @dependencies: swiper-bundle.min.js
 * @strict-mode: true
 */

(function () {
  const MOBILE_MQ = '(max-width: 767px)';
  const TRIGGER_SELECTOR = [
    '.product_stacked_gallery .photoswipe__image',
    '.product_stacked_gallery .gallery-image-container',
    '.product_stacked_gallery .gallery-zoom-button',
    '[data-product-images] .photoswipe__image',
    '[data-product-images] .product-image-clickable',
    '[data-product-images] .js-photoswipe__zoom',
  ].join(', ');

  /** @type {Map<string, PdpImageLightboxController>} */
  const controllersById =
    window.__PDP_IMAGE_LIGHTBOX_CONTROLLERS__ instanceof Map
      ? window.__PDP_IMAGE_LIGHTBOX_CONTROLLERS__
      : new Map();
  window.__PDP_IMAGE_LIGHTBOX_CONTROLLERS__ = controllersById;

  /**
   * @extends {HTMLElement}
   */
  class PdpImageLightboxController {
    /** @type {HTMLDialogElement|null} */
    dialog = null;

    /** @type {import('swiper').Swiper|null} */
    mainSwiper = null;

    /** @type {import('swiper').Swiper|null} */
    thumbsSwiper = null;

    /** @type {import('swiper').Swiper|null} */
    mobileSwiper = null;

    /** @type {boolean} */
    initialized = false;

    /** @type {HTMLElement|null} */
    lastFocus = null;

    /** @type {MediaQueryList|null} */
    mobileMq = null;

    /** @type {number} */
    scrollY = 0;

    /**
     * @param {HTMLDialogElement} dialog
     */
    constructor(dialog) {
      this.dialog = dialog;
      this.mobileMq = window.matchMedia(MOBILE_MQ);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
    }

    init() {
      if (!this.dialog) return;

      if (
        this.dialog.dataset.lightboxBound === 'true' &&
        this.dialog.id &&
        controllersById.has(this.dialog.id)
      ) {
        return;
      }

      this.dialog.dataset.lightboxBound = 'true';
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('selectstart', (event) => {
        // Block native browser selection (blue highlight) on media / thumbs while navigating
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('input, textarea, [contenteditable="true"]')) return;
        event.preventDefault();
      });

      this.dialog.querySelectorAll('[data-pdp-lightbox-close]').forEach((closeBtn) => {
        closeBtn.addEventListener('click', this.onCloseClick);
      });

      this.dialog.querySelectorAll('[data-pdp-lightbox-prev]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.slideBy(-1);
        });
      });

      this.dialog.querySelectorAll('[data-pdp-lightbox-next]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.slideBy(1);
        });
      });

      this.dialog.addEventListener('close', () => this.onClosed());

      if (this.mobileMq) {
        if (typeof this.mobileMq.addEventListener === 'function') {
          this.mobileMq.addEventListener('change', this.onBreakpointChange);
        } else if (typeof this.mobileMq.addListener === 'function') {
          this.mobileMq.addListener(this.onBreakpointChange);
        }
      }

      if (this.dialog.id) {
        controllersById.set(this.dialog.id, this);
      }
    }

    /**
     * @param {Element} trigger
     * @returns {number}
     */
    resolveIndex(trigger) {
      const stacked = trigger.closest('.product_stacked_gallery');

      if (stacked) {
        const el = trigger.closest('[data-index]') || trigger;
        const parsed = Number.parseInt(el.getAttribute('data-index') || '', 10);
        return Number.isNaN(parsed) ? 0 : Math.max(0, parsed - 1);
      }

      const zeroBased = trigger.closest(
        '.product-main-slide[data-index], .product-image-clickable[data-index]'
      );

      if (zeroBased) {
        const parsed = Number.parseInt(zeroBased.getAttribute('data-index') || '', 10);
        return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
      }

      const el = trigger.closest('[data-index]') || trigger;
      const parsed = Number.parseInt(el.getAttribute('data-index') || '', 10);
      if (Number.isNaN(parsed)) return 0;

      // Slideshow photoswipe images use 1-based data-index
      return Math.max(0, parsed - 1);
    }

    /**
     * @param {number} index
     */
    async open(index) {
      if (!this.dialog) return;

      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      await this.ensureSwipers();

      const isMobile = this.isMobile();
      const activeSwiper = isMobile ? this.mobileSwiper : this.mainSwiper;

      if (activeSwiper) {
        this.goToIndex(activeSwiper, index, 0);
      } else {
        this.syncThumbs(index);
      }

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.lockScroll();
      document.addEventListener('keydown', this.onKeyDown);

      // Recalculate sizes after dialog is visible
      requestAnimationFrame(() => {
        if (!this.isMobile() && this.mainSwiper) {
          this.mainSwiper.update();
          if (this.mainSwiper.params.loop) {
            this.mainSwiper.loopDestroy();
            this.mainSwiper.loopCreate();
          }
          this.thumbsSwiper?.update();
        }

        if (this.isMobile() && this.mobileSwiper) {
          this.mobileSwiper.update();
          if (this.mobileSwiper.params.loop) {
            this.mobileSwiper.loopDestroy();
            this.mobileSwiper.loopCreate();
          }
        }

        if (activeSwiper) {
          this.goToIndex(activeSwiper, index, 0);
        }
      });

      const closeBtn = this.dialog.querySelector(
        this.isMobile()
          ? '.pdp-image-lightbox__close--mobile'
          : '.pdp-image-lightbox__close--desktop'
      ) || this.dialog.querySelector('[data-pdp-lightbox-close]');

      if (closeBtn instanceof HTMLElement) {
        closeBtn.focus();
      }
    }

    /**
     * Clears accidental browser text/image selection (blue highlight on thumbs / main image).
     */
    clearMediaSelection() {
      const selection = window.getSelection?.();
      if (selection && selection.rangeCount > 0) {
        selection.removeAllRanges();
      }
    }

    /**
     * @param {import('swiper').Swiper} swiper
     * @param {number} index
     * @param {number} [speed]
     */
    goToIndex(swiper, index, speed) {
      if (!swiper) return;

      const total = Number.parseInt(this.dialog?.dataset.mediaCount || '0', 10) || 0;
      const safeIndex = total > 0 ? ((index % total) + total) % total : 0;

      if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(safeIndex, speed);
      } else {
        swiper.slideTo(safeIndex, speed);
      }

      this.syncThumbs(safeIndex);
      this.clearMediaSelection();
    }

    /**
     * @param {number} index
     */
    syncThumbs(index) {
      if (!this.thumbsSwiper || !this.dialog) return;

      const total = Number.parseInt(this.dialog.dataset.mediaCount || '0', 10) || 0;
      if (total < 1) return;

      const safeIndex = ((index % total) + total) % total;
      const slides = this.thumbsSwiper.slides;

      for (let i = 0; i < slides.length; i += 1) {
        slides[i].classList.toggle('swiper-slide-thumb-active', i === safeIndex);
      }

      if (typeof this.thumbsSwiper.update === 'function') {
        this.thumbsSwiper.update();
      }

      if (typeof this.thumbsSwiper.slideTo === 'function') {
        this.thumbsSwiper.slideTo(safeIndex);
      }
    }

    close() {
      if (!this.dialog) return;

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onClosed();
      }
    }

    onClosed() {
      this.unlockScroll();
      document.removeEventListener('keydown', this.onKeyDown);
      this.pauseVideos();

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    /**
     * Releases everything the controller bound outside its own dialog. Called when the dialog leaves
     * the document, which happens when a colorway swap replaces the product section.
     */
    dispose() {
      for (const swiper of [this.mainSwiper, this.thumbsSwiper, this.mobileSwiper]) {
        if (swiper && typeof swiper.destroy === 'function') {
          try {
            swiper.destroy(true, false);
          } catch (_error) {
            // Detached Swiper instances can throw; the dialog is already leaving the document.
          }
        }
      }

      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.mobileSwiper = null;

      document.removeEventListener('keydown', this.onKeyDown);

      if (this.mobileMq) {
        if (typeof this.mobileMq.removeEventListener === 'function') {
          this.mobileMq.removeEventListener('change', this.onBreakpointChange);
        } else if (typeof this.mobileMq.removeListener === 'function') {
          this.mobileMq.removeListener(this.onBreakpointChange);
        }
      }

      this.dialog = null;
      this.lastFocus = null;
    }

    lockScroll() {
      this.scrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('pdp-image-lightbox-open');
      document.body.classList.add('pdp-image-lightbox-open');
      document.body.style.top = `-${this.scrollY}px`;
    }

    unlockScroll() {
      document.documentElement.classList.remove('pdp-image-lightbox-open');
      document.body.classList.remove('pdp-image-lightbox-open');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY);
    }

    /**
     * @param {MouseEvent} event
     */
    onCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    /**
     * @param {MouseEvent} event
     */
    onDialogClick(event) {
      if (event.target === this.dialog) {
        this.close();
      }
    }

    /**
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (this.isMobile()) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.slideBy(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.slideBy(1);
      }
    }

    /**
     * @param {number} delta
     */
    slideBy(delta) {
      const swiper = this.isMobile() ? this.mobileSwiper : this.mainSwiper;
      if (!swiper) return;

      if (delta < 0) {
        swiper.slidePrev();
      } else {
        swiper.slideNext();
      }

      this.clearMediaSelection();
    }

    /**
     * @returns {boolean}
     */
    isMobile() {
      return Boolean(this.mobileMq && this.mobileMq.matches);
    }

    onBreakpointChange() {
      if (!this.dialog || !this.dialog.open) return;

      const index = this.isMobile()
        ? this.mainSwiper?.realIndex ?? 0
        : this.mobileSwiper?.realIndex ?? 0;

      this.ensureSwipers().then(() => {
        const swiper = this.isMobile() ? this.mobileSwiper : this.mainSwiper;
        if (swiper) {
          this.goToIndex(swiper, index, 0);
        }
      });
    }

    /**
     * @returns {Promise<void>}
     */
    async ensureSwipers() {
      await this.waitForSwiper();

      if (this.initialized) {
        this.mainSwiper?.update();
        this.thumbsSwiper?.update();
        this.mobileSwiper?.update();
        return;
      }

      this.initSwipers();
      this.initialized = true;
    }

    /**
     * @returns {Promise<void>}
     */
    waitForSwiper() {
      return new Promise((resolve) => {
        if (typeof window.Swiper !== 'undefined') {
          resolve();
          return;
        }

        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (typeof window.Swiper !== 'undefined' || attempts > 50) {
            window.clearInterval(timer);
            resolve();
          }
        }, 100);
      });
    }

    initSwipers() {
      if (typeof window.Swiper === 'undefined' || !this.dialog) return;

      const thumbsEl = this.dialog.querySelector('[data-pdp-lightbox-thumbs]');
      const mainEl = this.dialog.querySelector('[data-pdp-lightbox-main]');
      const mobileEl = this.dialog.querySelector('[data-pdp-lightbox-mobile]');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (thumbsEl) {
        const isGalleryLightbox = this.dialog.classList.contains('pdp-image-lightbox--gallery');

        this.thumbsSwiper = new window.Swiper(thumbsEl, {
          spaceBetween: isGalleryLightbox ? 16 : 13,
          slidesPerView: 'auto',
          watchSlidesProgress: true,
          centerInsufficientSlides: true,
          watchOverflow: true,
          allowTouchMove: true,
        });

        // Manual thumb → main (avoids broken Swiper thumbs + loop coupling)
        thumbsEl.addEventListener('click', (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;

          const slide = target.closest('.swiper-slide');
          if (!slide || !thumbsEl.contains(slide)) return;

          const slides = Array.from(thumbsEl.querySelectorAll('.swiper-slide'));
          const thumbIndex = slides.indexOf(slide);
          if (thumbIndex < 0 || !this.mainSwiper) return;

          event.preventDefault();
          this.goToIndex(this.mainSwiper, thumbIndex);
        });

        // Stop drag-select from starting on thumbs (mousedown + slight move selects images)
        thumbsEl.addEventListener('mousedown', (event) => {
          if (event.detail > 1) {
            event.preventDefault();
          }
        });
      }

      if (mainEl) {
        this.mainSwiper = new window.Swiper(mainEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: reduceMotion ? 0 : 350,
          loop: true,
          rewind: false,
          watchOverflow: true,
          resistanceRatio: 0,
          on: {
            slideChange: (swiper) => {
              this.syncThumbs(swiper.realIndex);
              this.pauseVideos();
              this.clearMediaSelection();
            },
          },
        });

        mainEl.addEventListener('mousedown', (event) => {
          if (event.detail > 1) {
            event.preventDefault();
          }
        });
      }

      if (mobileEl) {
        // Mobile-only: vertical gallery, 2 full images in viewport
        this.mobileSwiper = new window.Swiper(mobileEl, {
          direction: 'vertical',
          slidesPerView: 'auto',
          spaceBetween: 16,
          // Swiper's default touchstart preventDefault swallows the second finger, so pinch-zoom
          // never reaches the browser.
          touchStartPreventDefault: false,
          speed: reduceMotion ? 0 : 350,
          loop: true,
          rewind: false,
          autoHeight: false,
          mousewheel: true,
          grabCursor: true,
          resistanceRatio: 0,
          watchOverflow: true,
          on: {
            slideChange: () => {
              this.pauseVideos();
            },
          },
        });
      }
    }

    pauseVideos() {
      if (!this.dialog) return;

      this.dialog.querySelectorAll('video').forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });
    }
  }

  /**
   * @returns {void}
   */
  function bindDialogs() {
    // The registry outlives the DOM: a colorway swap replaces the product section, so an entry can
    // still point at the detached dialog of the previous product. Drop those before matching by id,
    // otherwise the new dialog is never bound and opening it throws because it has no document.
    for (const [id, controller] of controllersById) {
      if (controller.dialog && controller.dialog.isConnected) continue;

      controller.dispose();
      controllersById.delete(id);
    }

    document.querySelectorAll('[data-pdp-image-lightbox]').forEach((dialog) => {
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (dialog.id && controllersById.has(dialog.id)) return;
      const controller = new PdpImageLightboxController(dialog);
      controller.init();
    });
  }

  /**
   * @param {string} dialogId
   * @returns {PdpImageLightboxController|null}
   */
  function getController(dialogId) {
    bindDialogs();
    return controllersById.get(dialogId) || null;
  }

  /**
   * @returns {PdpImageLightboxController|null}
   */
  function getMainProductController() {
    bindDialogs();
    for (const entry of controllersById.values()) {
      if (entry.dialog && !entry.dialog.hasAttribute('data-trigger-root')) {
        return entry;
      }
    }
    return null;
  }

  const DRAG_THRESHOLD_PX = 8;
  let pointerStartX = 0;
  let pointerStartY = 0;
  let pointerDragged = false;

  /**
   * @param {PointerEvent} event
   */
  function onPointerDown(event) {
    pointerStartX = event.clientX;
    pointerStartY = event.clientY;
    pointerDragged = false;
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerMove(event) {
    if (pointerDragged) return;

    const dx = Math.abs(event.clientX - pointerStartX);
    const dy = Math.abs(event.clientY - pointerStartY);

    if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) pointerDragged = true;
  }

  /**
   * @param {MouseEvent} event
   */
  function onDocumentTriggerClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (pointerDragged) {
      pointerDragged = false;
      return;
    }

    if (target.closest('.product__video-wrapper iframe, .product__video-wrapper video')) {
      return;
    }

    const explicit = target.closest('[data-pdp-lightbox-trigger]');
    if (explicit) {
      const forId = explicit.getAttribute('data-pdp-lightbox-trigger');
      const controller = forId ? getController(forId) : null;
      if (!controller) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const rawIndex =
        explicit.getAttribute('data-lightbox-index') || explicit.getAttribute('data-index') || '0';
      const parsed = Number.parseInt(rawIndex, 10);
      controller.open(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
      return;
    }

    const trigger = target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    const controller = getMainProductController();
    if (!controller) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    controller.open(controller.resolveIndex(trigger));
  }

  /**
   * @returns {void}
   */
  function boot() {
    bindDialogs();

    if (!window.__PDP_IMAGE_LIGHTBOX_CLICK_BOUND__) {
      window.__PDP_IMAGE_LIGHTBOX_CLICK_BOUND__ = true;
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('pointermove', onPointerMove, true);
      document.addEventListener('click', onDocumentTriggerClick, true);
    }

    if (document.querySelector('[data-pdp-image-lightbox]:not([data-trigger-root])')) {
      document.querySelectorAll('.product_stacked_gallery').forEach((gallery) => {
        if (gallery instanceof HTMLElement) {
          gallery.dataset.stackedPsInitialized = 'true';
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
